import { Clutch } from '../api/clutches';
import { EggStatus } from '../utils/enums';
import { format } from 'date-fns';
import ClutchGeneticsPanel from './genetics/ClutchGeneticsPanel';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from './ui/BrandModal';

interface ViewClutchModalProps {
  isOpen: boolean;
  onClose: () => void;
  clutch: Clutch;
}

export default function ViewClutchModal({ isOpen, onClose, clutch }: ViewClutchModalProps) {
  const getStatusColor = (status: EggStatus) => {
    switch (status) {
      case EggStatus.LAID:
        return 'bg-cyan-100 text-cyan-800';
      case EggStatus.INCUBATING:
        return 'bg-amber-100 text-amber-800';
      case EggStatus.HATCHED:
        return 'bg-emerald-100 text-emerald-800';
      case EggStatus.FLEDGED:
        return 'bg-teal-100 text-teal-800';
      case EggStatus.BANDED:
        return 'bg-cyan-100 text-cyan-800';
      case EggStatus.NOT_FERTILIZED:
      case EggStatus.BROKEN:
      case EggStatus.ABANDONED:
      case EggStatus.DEAD_IN_SHELL:
      case EggStatus.DEAD_BEFORE_BANDING:
        return 'bg-rose-100 text-rose-800';
      default:
        return 'bg-violet-100 text-violet-800';
    }
  };

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title="Clutch Details" onClose={onClose} />
      <BrandModalBody>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <h4 className="text-sm font-medium text-slate-500">Date</h4>
            <p className="mt-1 text-sm text-slate-900">
              {format(new Date(clutch.date), 'MMMM d, yyyy')}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-slate-500">Breeding Pair</h4>
            <p className="mt-1 text-sm text-slate-900">
              {clutch.breeding_pair?.male_bird?.bandId || 'Unknown'} × {clutch.breeding_pair?.female_bird?.bandId || 'Unknown'}
            </p>
            <p className="text-sm text-slate-500">
              Cage: {clutch.breeding_pair?.cage_number || 'N/A'}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-slate-500">Species</h4>
            <p className="mt-1 text-sm text-slate-900">
              {clutch.breeding_pair?.male_bird?.species?.commonName || 'Unknown'} 
              {clutch.breeding_pair?.male_bird?.species?.commonName !== clutch.breeding_pair?.female_bird?.species?.commonName && 
                ` × ${clutch.breeding_pair?.female_bird?.species?.commonName || 'Unknown'}`}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-slate-500">Days Remaining Till Hatching</h4>
            <p className="mt-1 text-sm text-slate-900">
              {clutch.daysRemainingTillHatching !== null && clutch.daysRemainingTillHatching !== undefined ? (
                <span className={`font-semibold ${
                  clutch.daysRemainingTillHatching <= 0 
                    ? 'text-red-600' 
                    : clutch.daysRemainingTillHatching <= 7 
                      ? 'text-orange-600' 
                      : 'text-green-600'
                }`}>
                  {clutch.daysRemainingTillHatching <= 0 
                    ? `${Math.abs(clutch.daysRemainingTillHatching)} days overdue` 
                    : `${clutch.daysRemainingTillHatching} days`
                  }
                </span>
              ) : (
                <span className="text-slate-400">N/A</span>
              )}
            </p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-slate-500">Clutch ID</h4>
            <p className="mt-1 text-sm text-slate-900 font-mono">{clutch.id}</p>
          </div>

          <ClutchGeneticsPanel clutchId={clutch.id} eggCount={clutch.eggs?.length} />

          <div>
            <h4 className="text-sm font-medium text-slate-500">Eggs ({clutch.eggs?.length || 0})</h4>
            <div className="mt-2 space-y-3">
              {clutch.eggs && clutch.eggs.length > 0 ? (
                clutch.eggs.map((egg, index) => (
                  <div key={egg.id} className="border rounded-lg p-3 bg-slate-50/80">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-slate-900">
                        Egg #{index + 1}
                      </span>
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(egg.status)}`}>
                        {egg.status.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-slate-500">Laid:</span>
                        <p>{format(new Date(egg.layDate), 'MMM d, yyyy')}</p>
                      </div>
                      {egg.hatchingDate && (
                        <div>
                          <span className="text-slate-500">Hatched:</span>
                          <p>{format(new Date(egg.hatchingDate), 'MMM d, yyyy')}</p>
                        </div>
                      )}
                      {egg.bandNumber && (
                        <div>
                          <span className="text-slate-500">Band:</span>
                          <p>{egg.bandNumber}</p>
                        </div>
                      )}
                      {egg.incubationDays && (
                        <div>
                          <span className="text-slate-500">Incubation:</span>
                          <p>{egg.incubationDays} days</p>
                        </div>
                      )}
                      {egg.comment && (
                        <div className="col-span-2">
                          <span className="text-slate-500">Comment:</span>
                          <p>{egg.comment}</p>
                        </div>
                      )}
                      {egg.failureReason && (
                        <div className="col-span-2">
                          <span className="text-slate-500">Failure Reason:</span>
                          <p className="text-red-600">{egg.failureReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500 italic">No eggs recorded for this clutch</p>
              )}
            </div>
          </div>

          {(clutch.createdAt || clutch.updatedAt) && (
            <div>
              <h4 className="text-sm font-medium text-slate-500">Timestamps</h4>
              <div className="mt-1 text-sm text-slate-900">
                {clutch.createdAt && (
                  <p>Created: {format(new Date(clutch.createdAt), 'MMM d, yyyy h:mm a')}</p>
                )}
                {clutch.updatedAt && (
                  <p>Updated: {format(new Date(clutch.updatedAt), 'MMM d, yyyy h:mm a')}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </BrandModalBody>
      <BrandModalFooter>
        <button
          type="button"
          className="btn btn-secondary w-full sm:w-auto"
          onClick={onClose}
        >
          Close
        </button>
      </BrandModalFooter>
    </BrandModal>
  );
}
